import {
  AppointmentType,
  type Doctor,
  type MedicalAssistant,
  type OpsGlobalSettings,
} from "~/api/API";
import { selectMAByWeight } from "~/utils/autoAssignMACommon";

/**
 * Ops Settings defaults for report ops, pre-visit reviewer and billing ops on
 * an appointment the Portal clones itself (deposition hold, re-eval hold).
 * Same rules as the backend (`services/appointment_operations/ops_role_defaults.py`):
 * each role takes its own physician default, then its global default, only on
 * the types the ops role table gives it, skipping inactive members. A
 * supplemental's report op tries the physician's weighted supplemental
 * routing first, then the supplemental default, then global report ops (never
 * the physician's visit report ops default; Jason, 2026-10-01). Callers fill
 * only a role that is still blank.
 */
export interface OpsRoleDefaultsParams {
  appointmentType: AppointmentType | null;
  doctorId: string | null;
  allAssistants: MedicalAssistant[];
  allDoctors: Doctor[];
  globalSettings: OpsGlobalSettings | null;
}

export interface OpsRoleDefaults {
  reportOpId: string | null;
  preVisitReviewerId: string | null;
  billingOpId: string | null;
}

const NO_REPORT_OPS_TYPES = new Set<AppointmentType>([
  AppointmentType.DEPOSITION,
  AppointmentType.BRIEFING,
]);

const NO_PRE_VISIT_REVIEWER_TYPES = new Set<AppointmentType>([
  AppointmentType.DEPOSITION,
  AppointmentType.BRIEFING,
  AppointmentType.SUPPLEMENTAL,
]);

export const determineDefaultOpsRoles = ({
  appointmentType,
  doctorId,
  allAssistants,
  allDoctors,
  globalSettings,
}: OpsRoleDefaultsParams): OpsRoleDefaults => {
  const doctor = allDoctors.find((d) => d.id === doctorId);
  const firstActive = (
    ...candidates: (string | null | undefined)[]
  ): string | null => {
    for (const candidate of candidates) {
      const assistant = allAssistants.find((a) => a.id === candidate);
      if (assistant && !assistant.inactive) return assistant.id;
    }
    return null;
  };

  let reportOpId: string | null = null;
  if (appointmentType === AppointmentType.SUPPLEMENTAL) {
    const weighted = (doctor?.supplementalReportOpsAssignments ?? []).flatMap(
      (assignment) => {
        const member = allAssistants.find(
          (a) => a.id === assignment?.medicalAssistantID,
        );
        return assignment && member && !member.inactive
          ? [{ ma: member, weight: assignment.weightPercentage }]
          : [];
      },
    );
    reportOpId = firstActive(
      selectMAByWeight(weighted),
      doctor?.doctorDefaultSupplementalReportOpsId,
      globalSettings?.opsGlobalSettingsDefaultGlobalReportOpsId,
    );
  } else if (appointmentType && !NO_REPORT_OPS_TYPES.has(appointmentType)) {
    reportOpId = firstActive(
      doctor?.doctorDefaultReportOpsId,
      globalSettings?.opsGlobalSettingsDefaultGlobalReportOpsId,
    );
  }

  const preVisitReviewerId =
    appointmentType && !NO_PRE_VISIT_REVIEWER_TYPES.has(appointmentType)
      ? firstActive(
          doctor?.doctorDefaultPreVisitReviewerId,
          globalSettings?.opsGlobalSettingsDefaultGlobalPreVisitReviewerId,
        )
      : null;

  const billingOpId = firstActive(
    doctor?.doctorDefaultBillingOpsId,
    globalSettings?.opsGlobalSettingsDefaultGlobalBillingOpsId,
  );

  return { reportOpId, preVisitReviewerId, billingOpId };
};
